import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
// gsap.registerPlugin(ScrollTrigger) already happens in src/lib/gsap.js
import { gsap } from '../lib/gsap';
import './PlatformFlow.css';

import circleVial from '../assets/images/circle-vial.png';
import circleFibres from '../assets/images/circle-fibres.png';
import circleDelivery from '../assets/images/circle-delivery.png';

const steps = [
  { key: 'photosensitisers', img: circleVial,     title: 'Photosensitizers', sub: 'Light-activated agents', alt: 'Photosensitiser vial',  op: '+', halo: 'teal' },
  { key: 'fibres',           img: circleFibres,   title: 'Lasers & fibres',  sub: 'Light source',           alt: 'Optical fibres',        op: '+', halo: 'aqua' },
  { key: 'delivery',         img: circleDelivery, title: 'Delivery system',  sub: 'Engineered for PDT',     alt: 'PDT delivery system',   op: '=', halo: 'blue' },
];

// Fixed positions keep particles clear of labels and product images.
const PARTICLES = [
  { left: '18%', top: '22%', tone: 'teal' },
  { left: '29%', top: '68%', tone: 'aqua' },
  { left: '41%', top: '18%', tone: 'blue' },
  { left: '52%', top: '72%', tone: 'teal' },
  { left: '63%', top: '20%', tone: 'aqua' },
  { left: '74%', top: '70%', tone: 'blue' },
  { left: '84%', top: '26%', tone: 'teal' },
  { left: '35%', top: '84%', tone: 'aqua' },
];

const RESULT_WORDS = ['COMPLETE', 'PDT', 'SOLUTION'];

export default function PlatformFlow() {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      /* ── Reduced motion ────────────────────────────────────────────────
         Checked BEFORE anything is hidden. Everything is visible by
         default in CSS, so returning here leaves the section fully
         readable — which is also the fallback if JS never runs.        */
      const prefersReducedMotion =
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      const q = gsap.utils.selector(sectionRef);
      const line = q('[data-pdt-line]')[0];
      const energy = q('[data-pdt-energy]')[0];
      const isVertical = window.matchMedia('(max-width: 900px)').matches;

      /* ── Initial states ───────────────────────────────────────────────
         Set inside useGSAP (layout-effect timing) so it lands before
         first paint — no flash of the pre-animation state.            */
      gsap.set(line, isVertical ? { scaleY: 0 } : { scaleX: 0 });
      gsap.set(q('[data-pdt-step]'), { autoAlpha: 0, y: 18, scale: 0.94 });
      gsap.set(q('[data-pdt-img]'), { y: 6 });
      gsap.set(q('[data-pdt-label]'), { autoAlpha: 0, y: 8 });
      gsap.set(q('[data-pdt-symbol]'), { autoAlpha: 0, scale: 0.7 });
      gsap.set(q('[data-pdt-halo]'), { autoAlpha: 0, scale: 0.8 });
      gsap.set(q('[data-pdt-result]'), { autoAlpha: 0, scale: 0.9 });
      gsap.set(q('[data-pdt-result-word]'), { autoAlpha: 0, y: 10 });
      gsap.set(q('[data-pdt-ring]'), { drawSVG: undefined }); // no-op guard
      gsap.set(q('[data-pdt-particle]'), { autoAlpha: 0, scale: 0.6 });
      gsap.set(energy, { autoAlpha: 0 });

      // Ring draw uses stroke-dashoffset (no paid DrawSVG plugin needed).
      const ring = q('[data-pdt-ring]')[0];
      if (ring) {
        const len = ring.getTotalLength();
        gsap.set(ring, { strokeDasharray: len, strokeDashoffset: len });
      }

      /* Travel distance for the energy dot, resolved lazily so it stays
         correct after a resize. Vertical on mobile, horizontal above.  */
      const travel = (fraction) => () =>
        isVertical ? line.offsetHeight * fraction : line.offsetWidth * fraction;
      const axis = isVertical ? 'y' : 'x';

      /* ── One timeline for the whole section ───────────────────────── */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          once: true,
        },
        defaults: { ease: 'power2.out' },
        onComplete: () => {
          // Release will-change once the sequence is done.
          gsap.set(q('.pdt-step, .pdt-flow-line, .pdt-energy'), {
            willChange: 'auto',
          });
          startIdle();
        },
      });

      // STEP 2 — connector draws, teal → blue via CSS gradient
      tl.to(line, {
        [isVertical ? 'scaleY' : 'scaleX']: 1,
        duration: 1.6,
        ease: 'power1.inOut',
      });

      // Energy dot appears at the start of the line
      tl.to(energy, { autoAlpha: 1, duration: 0.25 }, 0.15);

      // Subtle particles, once only
      tl.to(
        q('[data-pdt-particle]'),
        { autoAlpha: 0.5, scale: 1, duration: 0.6, stagger: 0.07 },
        0.3
      ).to(
        q('[data-pdt-particle]'),
        { autoAlpha: 0, duration: 0.9, stagger: 0.05 },
        2.4
      );

      /* Reusable activation for one component.
         `at` = absolute time on the timeline. */
      const activate = (key, at) => {
        const step = q(`[data-pdt-step="${key}"]`)[0];
        const halo = step.querySelector('[data-pdt-halo]');
        const img = step.querySelector('[data-pdt-img]');
        const labels = step.querySelectorAll('[data-pdt-label]');

        tl.to(step, { autoAlpha: 1, y: 0, scale: 1, duration: 0.55 }, at)
          // Fibre circle gets a small rotational settle (Step 5)
          .fromTo(
            step,
            { rotation: key === 'fibres' ? -2 : 0 },
            { rotation: 0, duration: 0.5 },
            at
          )
          .to(img, { y: 0, duration: 0.5 }, at + 0.05)
          .to(halo, { autoAlpha: 1, scale: 1, duration: 0.4 }, at + 0.05)
          .to(halo, { autoAlpha: 0.55, duration: 0.6 }, at + 0.45)
          .to(labels, { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.06 }, at + 0.2);
      };

      /* Symbol pulse. `strong` is used for the equals sign (Step 8). */
      const pulseSymbol = (index, at, strong = false) => {
        const sym = q('[data-pdt-symbol]')[index];
        tl.to(sym, {
          autoAlpha: 1,
          scale: 1,
          duration: strong ? 0.35 : 0.28,
          ease: 'back.out(2.2)',
        }, at).to(sym, {
          scale: strong ? 1.18 : 1.12,
          duration: 0.14,
          yoyo: true,
          repeat: 1,
          ease: 'power1.inOut',
        }, at + 0.12);
      };

      // STEP 3 — Photosensitizers (energy already at position 0)
      activate('photosensitisers', 0.35);

      // STEP 4 — first plus
      pulseSymbol(0, 0.85);

      // STEP 5 — energy travels to fibres, then activates
      tl.to(energy, { [axis]: travel(0.33), duration: 0.45, ease: 'power1.inOut' }, 0.6);
      activate('fibres', 1.05);

      // STEP 6 — second plus
      pulseSymbol(1, 1.55);

      // STEP 7 — energy travels to delivery, then activates
      tl.to(energy, { [axis]: travel(0.66), duration: 0.45, ease: 'power1.inOut' }, 1.3);
      activate('delivery', 1.75);

      // STEP 8 — equals, slightly stronger pulse
      pulseSymbol(2, 2.25);

      // STEP 9 — energy reaches the result, ring draws clockwise
      tl.to(energy, { [axis]: travel(1), duration: 0.5, ease: 'power1.inOut' }, 2.0);

      const result = q('[data-pdt-result]')[0];
      const resultHalo = result.querySelector('[data-pdt-halo]');

      tl.to(result, { autoAlpha: 1, scale: 1, duration: 0.6 }, 2.45);
      if (ring) {
        tl.to(ring, { strokeDashoffset: 0, duration: 0.9, ease: 'power2.inOut' }, 2.45);
      }
      tl.to(resultHalo, { autoAlpha: 1, scale: 1, duration: 0.5 }, 2.5)
        .to(q('[data-pdt-result-word]'), {
          autoAlpha: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.1,
        }, 2.75)
        // One pulse, then settle
        .to(resultHalo, { scale: 1.1, autoAlpha: 0.85, duration: 0.45, ease: 'power1.inOut' }, 3.15)
        .to(resultHalo, { scale: 1, autoAlpha: 0.5, duration: 0.7 }, 3.6)
        // Energy dot retires
        .to(energy, { autoAlpha: 0, duration: 0.4 }, 3.0);

      /* ── Idle: 2–4px float, long and desynced ───────────────────────── */
      function startIdle() {
        q('[data-pdt-step]').forEach((step, i) => {
          gsap.to(step, {
            y: i % 2 === 0 ? -3 : -2,
            duration: 7 + i * 1.1,
            delay: i * 0.8,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
          });
        });

        // Single faint breathing glow on the final ring only.
        gsap.to(resultHalo, {
          autoAlpha: 0.68,
          duration: 4.5,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      }
    },
    { scope: sectionRef }
  );

  return (
    <div className="pdt-flow" ref={sectionRef}>
      {/* Connector — animated with scaleX (scaleY on mobile) */}
      <span className="pdt-flow-line" data-pdt-line aria-hidden="true" />

      {/* Travelling energy point */}
      <span className="pdt-energy" data-pdt-energy aria-hidden="true" />

      {/* Restrained one-shot particles */}
      <span className="pdt-particles" aria-hidden="true">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className={`pdt-particle pdt-particle--${p.tone}`}
            style={{ left: p.left, top: p.top }}
            data-pdt-particle
          />
        ))}
      </span>

      {steps.map((s) => (
        <div key={s.key} className="pdt-step" data-pdt-step={s.key}>
          <div className="pdt-circle-wrap">
            <span
              className={`pdt-halo pdt-halo--${s.halo}`}
              data-pdt-halo
              aria-hidden="true"
            />
            <div className="pdt-circle">
              <img src={s.img} alt={s.alt} data-pdt-img />
            </div>
          </div>
          <p className="pdt-step-title" data-pdt-label>{s.title}</p>
          <p className="pdt-step-sub" data-pdt-label>{s.sub}</p>
          <span className="pdt-op" data-pdt-symbol aria-hidden="true">{s.op}</span>
        </div>
      ))}

      <div className="pdt-step" data-pdt-step="result" data-pdt-result>
        <div className="pdt-circle-wrap">
          <span className="pdt-halo pdt-halo--result" data-pdt-halo aria-hidden="true" />
          <div className="pdt-circle pdt-circle--result">
            {/* Ring drawn clockwise via stroke-dashoffset */}
            <svg className="pdt-result-ring" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
              <circle cx="50" cy="50" r="48.5" data-pdt-ring
                      transform="rotate(-90 50 50)" />
            </svg>
            <span className="pdt-result-text">
              {RESULT_WORDS.map((w) => (
                <span key={w} className="pdt-result-word" data-pdt-result-word>{w}</span>
              ))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}